// ═══════════════════════════════════════════════════════════════
//  Login.jsx — Updated to use AuthContext
// ═══════════════════════════════════════════════════════════════

import React, { useState } from "react";
import { useAuth } from "../../../AuthProvider/AuthContext";
import { Eye, EyeOff } from "lucide-react";

export default function Login({ onClose, onSwitchToSignup }) {
  const { login } = useAuth();

  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleLogin = async () => {
    setError("");
    const normalizedEmail = email.trim();

    if (!normalizedEmail || !/\S+@\S+\.\S+/.test(normalizedEmail))
      return setError("Enter a valid email address");
    if (!password)
      return setError("Password is required");

    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ email: normalizedEmail, password }),
      });
      const data = await res.json();

      if (!res.ok || !data.token || !data.user) {
        const adminRes = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: normalizedEmail, password }),
        });
        const adminData = await adminRes.json();

        if (adminRes.ok && adminData.token) {
          localStorage.setItem("mbt_admin_auth", JSON.stringify({ token: adminData.token }));
          onClose();
          window.location.assign("/admin");
          return;
        }

        setError(data.error || adminData.error || "Login failed");
      } else {
        // ── Save to context + localStorage in one call ──────
        login(data.user, data.token);
        onClose();
      }
    } catch {
      setError("Could not connect to server");
    } finally {
      setLoading(false);
    }
  };

  // Allow Enter key to submit
  const onKeyDown = (e) => { if (e.key === "Enter") handleLogin(); };

  return (
    <div className="nb-overlay" onClick={onClose}>
      <div
        className="nb-modal bg-light"
        style={{backgroundColor:"white"}}
        data-aos="zoom-in"
        data-aos-duration="300"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="nb-modal__close" onClick={onClose}>×</button>
        <div className="nb-modal__badge">Welcome back 🍯</div>
        <h2 className="nb-modal__title">Log in to MakeBeeTrip</h2>
        <p className="nb-modal__sub">Good to see you again</p>

        {error && (
          <p className="nb-modal__error">{error}</p>
        )}

        <div className="nb-field">
          <label>Email address</label>
          <input
            type="email"
            placeholder="riya@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={onKeyDown}
            autoFocus
          />
        </div>
        <div className="nb-field">
  <label>Password</label>

  <div className="password-input-wrap">
    <input
      type={showPass ? "text" : "password"}
      placeholder="Your password"
      value={password}
      onChange={(e) => setPassword(e.target.value)}
      onKeyDown={onKeyDown}
    />

    <button
      type="button"
      className="password-toggle"
      onClick={() => setShowPass(prev => !prev)}
      aria-label={showPass ? "Hide password" : "Show password"}
    >
      {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  </div>
</div>

        <p className="nb-modal__forgot">Forgot password?</p>

        <button
          className="nb-btn nb-btn--primary nb-btn--full nb-modal__submit"
          onClick={handleLogin}
          disabled={loading}
        >
          {loading ? "Logging in…" : "Log in"}
        </button>

        <div className="nb-modal__divider"><span>or</span></div>

        <div className="nb-modal__social">
          <button
  type="button"
  className="nb-social-btn"
  onClick={() => {
    window.location.href = `${process.env.REACT_APP_API_URL}/api/auth/google`;
  }}
>
            <svg width="16" height="16" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>
        </div>

        <p className="nb-modal__footer">
          Don't have an account?{" "}
          <span onClick={() => { onClose(); onSwitchToSignup(); }}>Sign up</span>
        </p>
      </div>
    </div>
  );
}
